/**
 * Team Login (staff / admin). Google-only, no password fields.
 *
 * Permanent URL: ${APP_BASE_URL}/login (routed via /login AND legacy /).
 *
 * Auth model: Emergent-managed Google OAuth. The Google email must
 * match an accepted staff invitation. Uninvited emails, disabled
 * accounts, and customer-type users are denied server-side by
 * `require_admin` + the login redirect logic in AuthCallback.
 */
import React from "react";
import { Link } from "react-router-dom";
import { GasPump, ShieldCheck, ChartLine, Truck, UsersThree } from "@phosphor-icons/react";

export default function Login() {
  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white" data-testid="team-login-page">
      {/* Left panel - industrial image with overlay */}
      <div className="hidden lg:block relative">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1683299626812-5e47ed3d0f17?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwZnVlbCUyMHJlZmluZXJ5JTIwcGlwZWxpbmUlMjBzdW5zZXR8ZW58MHx8fHwxNzc2ODAxNjUwfDA&ixlib=rb-4.1.0&q=85)",
          }}
        />
        <div className="absolute inset-0" style={{ background: "rgba(9,9,11,0.82)" }} />
        <div className="relative z-10 h-full p-10 flex flex-col justify-between text-white">
          <div>
            <div className="text-2xl font-bold font-display" data-testid="brand-mark">
              GetFuel<span style={{ color: "#f97316" }}>.</span>
            </div>
            <div className="overline mt-2 text-white/60">
              FUEL CARD &amp; BILLING PLATFORM
            </div>
          </div>
          <div>
            <div
              className="text-5xl font-bold leading-tight font-display"
              style={{ fontFamily: "Chivo" }}
            >
              One system.<br />
              Every <span style={{ color: "#f97316" }}>litre</span> accounted for.
            </div>
            <div className="mt-6 grid grid-cols-2 gap-4 text-sm text-white/70">
              <div className="flex items-center gap-2"><Truck size={18} /> Multi-fleet</div>
              <div className="flex items-center gap-2"><GasPump size={18} /> Esso + Pipeline</div>
              <div className="flex items-center gap-2"><ChartLine size={18} /> Realtime margins</div>
              <div className="flex items-center gap-2"><ShieldCheck size={18} /> Audit-grade</div>
            </div>
          </div>
          <div className="overline text-white/40">
            &copy; GETFUEL / WELOCITY LOGISTICS &amp; SOLUTIONS
          </div>
        </div>
      </div>

      {/* Right - login form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md gf-card" style={{ borderRadius: 2 }}>
          <div className="gf-card-body">
            <div className="flex items-center gap-2 mb-1">
              <UsersThree size={18} weight="bold" className="text-zinc-700" />
              <div className="overline" data-testid="login-overline">TEAM LOGIN</div>
            </div>
            <h1 className="text-3xl font-bold font-display mt-1 mb-1" data-testid="login-heading">
              Team Login
            </h1>
            <p className="text-sm text-zinc-500 mb-8">
              Sign in with your Google account. Access is restricted to
              staff whose Google email matches an accepted team
              invitation.
            </p>

            <button
              onClick={handleGoogle}
              data-testid="google-signin-button"
              className="gf-btn w-full justify-center gf-btn-primary"
              style={{ padding: "12px 16px", fontSize: 14 }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21.8 10.2H12v3.9h5.6c-.2 1.4-1.7 4-5.6 4-3.4 0-6.1-2.8-6.1-6.2s2.7-6.2 6.1-6.2c1.9 0 3.2.8 3.9 1.5l2.7-2.6C16.9 2.9 14.7 2 12 2 6.9 2 2.8 6.1 2.8 12s4.1 10 9.2 10c5.3 0 8.8-3.7 8.8-9 0-.6-.1-1.2-.2-1.8z" fill="#fff"/>
              </svg>
              Continue with Google
            </button>

            <div className="mt-6 text-xs text-zinc-500 space-y-1">
              <div>
                Team accounts require a Super Admin invitation. If you
                don&apos;t have one, contact your administrator.
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-zinc-200 text-xs text-zinc-500">
              Are you a customer? Go to the{" "}
              <Link
                to="/customer/login"
                className="text-orange-600 font-medium hover:underline"
                data-testid="link-to-customer-login"
              >
                Customer Login
              </Link>
              .
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
